import React, {useEffect, useState} from 'react'
import {
    Drawer,
    DrawerOverlay,
    Button,
    Text,
    DrawerContent,
    DrawerBody,
    DrawerHeader,
    DrawerFooter,
    Stack,
    useStyleConfig,
    useBreakpointValue,
    Flex,
    RadioGroup,
    Radio,
    Progress,
    Box,
} from '@chakra-ui/react'
import {CloseIcon} from '../../../components/icons'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import FreeItemPromoTile from './free-item-promo-tile'
import QuantityPicker from '../../../components/quantity-picker'
import {remCalc} from '../../../theme/_utils'
import {
    useShopperBasketsMutationHelper,
    useAccessToken,
    useCommerceApi,
} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import LoadingSpinner from '../../../components/loading-spinner'

const FreeItemPromoDrawer = ({
    onClose,
    isOpen,
    setSelectedFreeItems,
    selectedPromotion = {},
    basketItems = [],
    handleRemoveItem,
}) => {
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {refetch} = useCurrentBasket()
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const styles = useStyleConfig('ProductFilterDrawer', {variant: 'freeItemPromo'})
    const drawerPlacement = useBreakpointValue({base: 'bottom', md: 'right'})

    const [promoProducts, setPromoProducts] = useState([])
    const [thisPromoItems, setThisPromoItems] = useState([])
    const [isLoading, setIsLoading] = useState(false)

    const arrChunker = (arr, size = 24) => {
        const chunks = []
        for (let i = 0; i < arr.length; i += size) {
            chunks.push(arr.slice(i, i + size))
        }
        return chunks
    }

    const fetchProducts = async () => {
        try {
            setIsLoading(true)
            const ids = selectedPromotion?.bonusProducts?.map((p) => p?.productId) || []
            const chunkedIds = arrChunker(ids, 24)
            const token = await getTokenWhenReady()

            const responses = await Promise.all(
                chunkedIds.map((batch) =>
                    api.shopperProducts.getProducts({
                        parameters: {
                            ids: batch.join(','),
                        },
                        expand: ['availability', 'images'],
                        headers: {Authorization: `Bearer ${token}`},
                    }),
                ),
            )
            const products = responses.flatMap((res) => res?.data || [])
            const productsWithStock = products?.filter((product) => {
                return product?.inventory?.stockLevel > 0
            })
            setIsLoading(false)
            return productsWithStock
        } catch (e) {
            setIsLoading(false)
            console.error('Error getting products for promotion', e)
            return []
        }
    }

    const refreshBasket = async () => {
        try {
            await refetch()
        } catch (error) {
            console.error('Failed to refresh basket', error)
        }
    }

    useEffect(() => {
        if (!selectedPromotion?.id) return

        const init = async () => {
            await refreshBasket()

            const res = await fetchProducts()
            if (res?.length) {
                setPromoProducts(res)
            }
        }

        init()
    }, [selectedPromotion])

    // Sync thisPromoItems with actual basket state when drawer opens or basket changes
    useEffect(() => {
        if (!selectedPromotion?.id || !promoProducts.length) return

        const promoBasketItems = basketItems.filter(
            (item) => item?.bonusDiscountLineItemId === selectedPromotion.id,
        )

        const itemsByProduct = {}
        for (const basketItem of promoBasketItems) {
            itemsByProduct[basketItem.productId] = (itemsByProduct[basketItem.productId] || 0) + 1
        }

        const synced = Object.entries(itemsByProduct).map(([productId, qty]) => {
            const product = promoProducts.find((p) => p.id === productId)
            return product ? {...product, quantity: qty} : {id: productId, quantity: qty}
        })

        setThisPromoItems(synced)
    }, [selectedPromotion?.id, basketItems, promoProducts])

    const promotionId = selectedPromotion?.id || ''
    const allowedItems = selectedPromotion?.maxBonusItems || 0
    const isSingleSelect = allowedItems === 1

    const totalQuantity = thisPromoItems.reduce((sum, item) => sum + (item.quantity || 1), 0)

    const getProductQuantity = (productId) => {
        const item = thisPromoItems.find((i) => i.id === productId)
        return item?.quantity || 0
    }

    const handleSelect = (product) => {
        if (isSingleSelect) {
            setThisPromoItems([{...product, quantity: 1}])
            return
        }
        handleQuantityChange(product, 1)
    }

    const handleQuantityChange = (product, delta) => {
        setThisPromoItems((prev) => {
            const existing = prev.find((item) => item.id === product.id)
            const currentQty = existing ? existing.quantity || 1 : 0
            const newQty = currentQty + delta

            if (newQty < 0) return prev

            const totalOther = prev.reduce((sum, item) => {
                if (item.id === product.id) return sum
                return sum + (item.quantity || 1)
            }, 0)

            if (totalOther + newQty > allowedItems) return prev

            if (newQty === 0) {
                const basketPromoItem = basketItems.find(
                    (item) =>
                        item.productId === product.id &&
                        item?.bonusDiscountLineItemId === promotionId,
                )
                if (basketPromoItem) {
                    handleRemoveItem(
                        basketPromoItem,
                        basketPromoItem?.c_variantIdentifier ||
                            basketPromoItem?.name ||
                            basketPromoItem?.productName,
                        100,
                        false,
                    )
                }
                return prev.filter((item) => item.id !== product.id)
            }

            if (existing) {
                return prev.map((item) =>
                    item.id === product.id ? {...item, quantity: newQty} : item,
                )
            }

            return [...prev, {...product, quantity: newQty}]
        })
    }

    const handleAddGiftToCart = async () => {
        const selectedItems = thisPromoItems || []
        if (!selectedItems.length) return

        const allProductItems = selectedItems.flatMap((item) => {
            const itemId = item.id || item.productId
            const desiredQty = item.quantity || 1

            const currentBasketQty = basketItems.filter(
                (basketItem) =>
                    basketItem.productId === itemId &&
                    basketItem?.bonusDiscountLineItemId === promotionId,
            ).length

            const additionalQty = desiredQty - currentBasketQty
            if (additionalQty <= 0) return []

            return Array.from({length: additionalQty}, () => ({
                productId: itemId,
                price: item.price,
                quantity: 1,
                bonusDiscountLineItemId: promotionId,
                bonusProductLineItem: item?.bonusProductLineItem,
                c_MaterialGroup: item?.c_MaterialGroup,
                c_subModelIdentifier: item?.c_subModelIdentifier,
            }))
        })

        try {
            if (allProductItems.length) {
                await addItemToNewOrExistingBasket(allProductItems)
            }

            setSelectedFreeItems((prev) => ({
                ...prev,
                [promotionId]: {
                    ...prev[promotionId],
                    items: thisPromoItems,
                },
            }))
        } catch (e) {
            console.error('Failed to add free items to basket', e)
        } finally {
            await refetch()
            onClose()
        }
    }

    return (
        <Drawer
            variant="filter"
            isOpen={isOpen}
            onClose={onClose}
            placement={drawerPlacement}
            closeOnEsc
            closeOnOverlayClick
        >
            <DrawerOverlay />
            <DrawerContent>
                {isSingleSelect && (
                    <DrawerHeader {...styles?.header}>
                        <Flex {...styles?.titleContainer}>
                            <Text {...styles?.headerText} w="100%">
                                <FormattedMessage
                                    id="cart.free_item_promo_drawer.header_title"
                                    defaultMessage="Select a free product!"
                                />
                            </Text>
                            <CloseIcon
                                data-testid="close-icon"
                                onClick={onClose}
                                {...styles?.closeButton}
                            />
                        </Flex>
                    </DrawerHeader>
                )}
                {!isSingleSelect && (
                    <DrawerHeader {...styles?.headerMulti}>
                        <Flex flexDirection={'column'} w="100%">
                            <Flex {...styles?.titleContainerMulti}>
                                <Text {...styles?.headerText} w="100%">
                                    <FormattedMessage
                                        id="cart.free_item_promo_drawer.header_title_plural"
                                        defaultMessage="Select {count} free products!"
                                        values={{count: allowedItems}}
                                    />
                                </Text>
                                <CloseIcon
                                    data-testid="close-icon"
                                    onClick={onClose}
                                    {...styles?.closeButton}
                                />
                            </Flex>
                            <Flex flexDirection="column" w="100%" gap={remCalc(16)}>
                                <Flex
                                    position="relative"
                                    h={remCalc(16)}
                                    borderRadius={remCalc(8)}
                                    bg="background.light"
                                    alignItems="center"
                                >
                                    {totalQuantity === 0 ? (
                                        <Box
                                            w={remCalc(12)}
                                            h={remCalc(12)}
                                            ml={remCalc(2)}
                                            borderRadius="50%"
                                            bg="background.dark"
                                            flexShrink={0}
                                        />
                                    ) : (
                                        <Progress
                                            value={(totalQuantity / allowedItems) * 100}
                                            h={remCalc(12)}
                                            w="100%"
                                            bg="transparent"
                                            mx={remCalc(2)}
                                            sx={{
                                                '& > div': {
                                                    backgroundColor: 'background.dark',
                                                    borderRadius: remCalc(6),
                                                },
                                            }}
                                        />
                                    )}
                                </Flex>
                                {totalQuantity < allowedItems ? (
                                    <Text {...styles?.remainingText}>
                                        <FormattedMessage
                                            id="cart.free_item_promo_drawer.remaining_count"
                                            defaultMessage="You can still select {count} {count, plural, one {product} other {products}}"
                                            values={{count: allowedItems - totalQuantity}}
                                        />
                                    </Text>
                                ) : (
                                    <Text {...styles?.remainingText}>
                                        <FormattedMessage
                                            id="cart.free_item_promo_drawer.all_selected"
                                            defaultMessage="You have selected all {count} free gift products"
                                            values={{count: allowedItems}}
                                        />
                                    </Text>
                                )}
                            </Flex>
                        </Flex>
                    </DrawerHeader>
                )}

                <DrawerBody {...styles?.body}>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : isSingleSelect && promoProducts?.length > 0 ? (
                        <RadioGroup
                            value={thisPromoItems[0]?.id || ''}
                            width="100%"
                            onChange={(val) => {
                                const p = promoProducts.find((pp) => pp.id === val)
                                if (p) handleSelect(p)
                            }}
                        >
                            <Stack gap={remCalc(16)} paddingTop={remCalc(16)}>
                                {promoProducts.map((product) => (
                                    <Flex
                                        key={`${promotionId}-${product.id}`}
                                        onClick={(e) => {
                                            if (e?.target?.closest('[data-stop-propagation]'))
                                                return
                                            handleSelect(product)
                                        }}
                                    >
                                        <FreeItemPromoTile
                                            product={product}
                                            actionButton={
                                                <Radio
                                                    value={product.id}
                                                    display="inline-flex"
                                                    height={{base: '44px', lg: '24px'}}
                                                    borderWidth={remCalc(0.6)}
                                                    isChecked={thisPromoItems.some(
                                                        (item) => item.id === product.id,
                                                    )}
                                                    data-stop-propagation
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            }
                                        />
                                    </Flex>
                                ))}
                            </Stack>
                        </RadioGroup>
                    ) : (
                        <Stack gap={remCalc(16)} paddingTop={remCalc(16)} width="100%">
                            {promoProducts.map((product) => {
                                const qty = getProductQuantity(product.id)
                                const hideQtyPicker = qty === 0 && totalQuantity >= allowedItems
                                return (
                                    <Flex key={`${promotionId}-${product.id}`}>
                                        <FreeItemPromoTile
                                            product={product}
                                            actionButton={
                                                hideQtyPicker ? null : (
                                                    <QuantityPicker
                                                        value={qty}
                                                        min={0}
                                                        max={qty + (allowedItems - totalQuantity)}
                                                        step={1}
                                                        onChange={(_str, num) => {
                                                            const delta = num - qty
                                                            if (delta !== 0)
                                                                handleQuantityChange(product, delta)
                                                        }}
                                                        productName={
                                                            product?.c_variantIdentifier ||
                                                            product?.name ||
                                                            product?.productName
                                                        }
                                                        variant="freeItemPromo"
                                                    />
                                                )
                                            }
                                        />
                                    </Flex>
                                )
                            })}
                        </Stack>
                    )}
                </DrawerBody>

                <DrawerFooter display="block" width="full" height="auto">
                    <Stack>
                        <Button
                            width="full"
                            variant="primary"
                            isDisabled={totalQuantity < allowedItems}
                            onClick={handleAddGiftToCart}
                        >
                            <FormattedMessage
                                id="cart.free_item_promo_drawer.footer.button_text"
                                defaultMessage="Return to the cart"
                            />
                        </Button>
                    </Stack>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    )
}

export default FreeItemPromoDrawer

FreeItemPromoDrawer.propTypes = {
    onClose: PropTypes.func,
    isOpen: PropTypes.bool,
    promoProducts: PropTypes.array,
    setSelectedFreeItems: PropTypes.func,
    selectedPromotion: PropTypes.object,
    basketItems: PropTypes.array,
    basketId: PropTypes.string,
    handleRemoveItem: PropTypes.func,
}
